import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { saveSystem } from '@/systems/SaveSystem';
import type { PerTableStats } from '@/types';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

export interface TimeAttackResultPayload {
  score: number;
  correctCount: number;
  wrongCount: number;
  speedBonusRate: number;
  maxCombo: number;
  totalCorrectResponseTimeMs: number;
  perTable: Record<number, PerTableStats>;
  durationMs: number;
}

/**
 * TimeAttackResultScene — fim de uma rodada de Contra o Tempo.
 *
 * Persiste a sessão (XP, perTable, streakRecord) no SaveSystem e tenta
 * atualizar o recorde pessoal do modo. Se bateu, exibe o badge "Novo
 * recorde!" animado e dispara fanfarra de rankUp como reforço positivo.
 */
export class TimeAttackResultScene extends Phaser.Scene {
  private payload!: TimeAttackResultPayload;
  private xpLabel?: Phaser.GameObjects.Text;
  private rankUpLabel?: Phaser.GameObjects.Text;
  private newRecordLabel?: Phaser.GameObjects.Text;
  private recordSummaryLabel?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'TimeAttackResultScene' });
  }

  init(data: TimeAttackResultPayload): void {
    this.payload = data;
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height * 0.14, 'Contra o Tempo', {
        fontFamily: Typography.fontFamily,
        fontSize: '40px',
        color: Palette.accentCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.21, 'Tempo encerrado!', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.34, `${this.payload.score} pontos`, {
        fontFamily: Typography.fontFamily,
        fontSize: '72px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.newRecordLabel = this.add
      .text(width / 2, height * 0.42, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '32px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.recordSummaryLabel = this.add
      .text(width / 2, height * 0.48, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '18px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5);

    this.renderStatBlock();

    this.xpLabel = this.add
      .text(width / 2, height * 0.7, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.rankUpLabel = this.add
      .text(width / 2, height * 0.76, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    new Button(this, width / 2 - 160, height * 0.88, 'Tentar de novo', {
      width: 280,
      height: 64,
      fontSize: 22,
      onClick: () => fadeToScene(this, 'TimeAttackScene'),
    });

    new Button(this, width / 2 + 160, height * 0.88, 'Voltar ao menu', {
      width: 280,
      height: 64,
      fontSize: 22,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.accent,
      onClick: () => fadeToScene(this, 'MenuScene'),
    });

    void this.persistSession();
  }

  private renderStatBlock(): void {
    const { width, height } = this.scale;
    const lines = [
      `${this.payload.correctCount} corretas · ${this.payload.wrongCount} erros`,
      `Melhor combo: ${this.payload.maxCombo}`,
      `${Math.round(this.payload.speedBonusRate * 100)}% no modo turbo`,
    ];
    lines.forEach((line, i) => {
      this.add
        .text(width / 2, height * 0.55 + i * 26, line, {
          fontFamily: Typography.fontFamily,
          fontSize: '20px',
          color: Palette.textMutedCss,
        })
        .setOrigin(0.5);
    });
  }

  private async persistSession(): Promise<void> {
    try {
      // 1. Sessão geral: XP, perTable e streakRecord acumulam.
      const outcome = await saveSystem.recordSession({
        scoreGained: this.payload.score,
        correctCount: this.payload.correctCount,
        wrongCount: this.payload.wrongCount,
        totalCorrectResponseTimeMs: this.payload.totalCorrectResponseTimeMs,
        maxCombo: this.payload.maxCombo,
        perTable: this.payload.perTable,
        // Sem levelKey/stars: Contra o Tempo não desbloqueia fases.
      });

      // 2. Recorde específico do Contra o Tempo.
      const record = await saveSystem.updateTimeAttackBest(this.payload.score);

      if (!this.scene.isActive()) return;

      this.recordSummaryLabel?.setText(
        record.newRecord
          ? `Recorde anterior: ${record.previous} pts`
          : `Recorde atual: ${record.current} pts`,
      );

      if (record.newRecord && this.newRecordLabel) {
        this.newRecordLabel.setText('Novo recorde! 🎉');
        this.tweens.add({
          targets: this.newRecordLabel,
          alpha: 1,
          scale: { from: 0.7, to: 1 },
          duration: 480,
          ease: 'Back.easeOut',
        });
        audioManager.playSfx('rankUp');
      }

      if (outcome.xpGained > 0 && this.xpLabel) {
        this.xpLabel.setText(`+${outcome.xpGained} XP`);
        this.tweens.add({
          targets: this.xpLabel,
          alpha: 1,
          y: this.xpLabel.y - 10,
          duration: 420,
          delay: record.newRecord ? 320 : 0,
          ease: 'Cubic.easeOut',
        });
      }

      if (outcome.rankUp && this.rankUpLabel) {
        this.rankUpLabel.setText(`Subiu de rank! ${outcome.rankAfter.name}`);
        this.tweens.add({
          targets: this.rankUpLabel,
          alpha: 1,
          scale: { from: 0.7, to: 1 },
          duration: 480,
          delay: 600,
          ease: 'Back.easeOut',
        });
        // Se já tocou rankUp do recorde, não duplica — usamos levelComplete.
        const sfx = record.newRecord ? 'levelComplete' : 'rankUp';
        this.time.delayedCall(600, () => audioManager.playSfx(sfx));
      }
    } catch (err) {
      console.error('[TimeAttackResultScene] Falha ao persistir sessão', err);
    }
  }
}
